import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  Skeleton,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Button
} from "@material-ui/core";
import {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from "@material-ui/lab";
import { styled } from "@material-ui/core/styles";
import { Icon } from "@iconify/react";
import messageFill from "@iconify/icons-eva/message-circle-fill";
import phoneFill from "@iconify/icons-eva/phone-call-fill";
import calendarFill from "@iconify/icons-eva/calendar-fill";
import checkmarkFill from "@iconify/icons-eva/checkmark-circle-2-fill";
import heartFill from "@iconify/icons-eva/heart-fill";
import filterFill from "@iconify/icons-eva/funnel-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import arrowIosUpwardFill from "@iconify/icons-eva/arrow-ios-upward-fill";
import fileTextFill from "@iconify/icons-eva/file-text-fill";
import homeFill from "@iconify/icons-eva/home-fill";
import currencyFill from "@iconify/icons-eva/credit-card-fill";
import personFill from "@iconify/icons-eva/person-fill";
import { format, formatDistanceToNow } from "date-fns";
import { he } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import useWebotServer from "../../../../../hooks/useWebotServer";
import {
  getContactCrmIsHot,
  getContactCrmStatus,
  getContactCustomerTypes
} from "../../../../../@types/sharedSchema";
import { ContactTableCustomerChip } from "./ContactTableCustomerChip";
import { ContactCrmStatusChip } from "./ContactCrmStatusChip";
import type {
  REST_schema,
  RealEstateContact,
  ProcessedListingAddress
} from "../../../../../@types/sharedSchema";
import Label from "../../../../Label";
import { numberToCurrency } from "../../../../../utils/listings/display";

type ContactTimelineAction = REST_schema.ContactTimelineAction;
type ContactObjectAcrossUsers = REST_schema.ContactObjectAcrossUsers;

const StyledTimelineItem = styled(TimelineItem)(({ theme }) => ({
  "&:before": {
    display: "none"
  },
  minHeight: 40,
  "& .MuiTimelineContent-root": {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  }
}));

const CompactTimelineContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  minHeight: 32
}));

const UserSection = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  overflow: "hidden"
}));

const UserWidgetCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main
  }
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  height: 24,
  "& .MuiChip-label": {
    fontSize: "0.75rem",
    fontWeight: 600
  }
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`
}));

interface ContactTimelineProps {
  phoneNumberInternational: string;
}

const ACTION_TYPES = [
  "message-sent",
  "call",
  "meeting",
  "task",
  "favorite-listings-minisite-creation"
] as const;

function getTimelineIcon(actionType: string) {
  switch (actionType) {
    case "message-sent":
      return messageFill;
    case "call":
      return phoneFill;
    case "meeting":
      return calendarFill;
    case "task":
      return checkmarkFill;
    case "favorite-listings-minisite-creation":
      return heartFill;
    default:
      return messageFill;
  }
}

function getTimelineColor(actionType: string) {
  switch (actionType) {
    case "message-sent":
      return "primary";
    case "call":
      return "success";
    case "meeting":
      return "info";
    case "task":
      return "warning";
    case "favorite-listings-minisite-creation":
      return "error";
    default:
      return "primary";
  }
}

export function ContactTableNestedRowTimeline({
  phoneNumberInternational
}: ContactTimelineProps): JSX.Element {
  const { t } = useTranslation();
  const { getContactDetails } = useWebotServer();
  const [contactDetails, setContactDetails] =
    useState<REST_schema.ContactDetailsResponseSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleActionTypes, setVisibleActionTypes] = useState<Set<string>>(new Set(ACTION_TYPES));
  const [visibleUsers, setVisibleUsers] = useState<Set<string>>(new Set());
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);

  useEffect(() => {
    const fetchContactDetails = async () => {
      if (!phoneNumberInternational) return;

      setLoading(true);
      setError(null);

      try {
        const details = await getContactDetails(phoneNumberInternational);
        setContactDetails(details);

        // Initialize visible users with all users
        if (details && details.length > 0) {
          const allUserIds = new Set<string>();
          details.forEach((detail) => {
            detail.timeline?.forEach((action) => {
              allUserIds.add(action.actionUser.userId);
            });
          });
          setVisibleUsers(allUserIds);
        }
      } catch (err) {
        console.error("Error fetching contact details:", err);
        setError(
          t("contactsPage.timeline.errorFetching", {
            defaultValue: "Failed to load contact timeline"
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, [phoneNumberInternational, getContactDetails, t]);

  function formatActionTimeAgo(actionTime?: Date): string {
    if (!actionTime) return "";
    return formatDistanceToNow(new Date(actionTime), {
      addSuffix: true,
      locale: he
    });
  }

  function formatActionTime(actionTime?: Date): string {
    if (!actionTime) return "";
    return format(new Date(actionTime), "dd/MM/yyyy HH:mm", { locale: he });
  }

  function handleActionTypeToggle(actionType: string) {
    const newVisible = new Set(visibleActionTypes);
    if (newVisible.has(actionType)) {
      newVisible.delete(actionType);
    } else {
      newVisible.add(actionType);
    }
    setVisibleActionTypes(newVisible);
  }

  function handleUserToggle(userId: string) {
    const newVisible = new Set(visibleUsers);
    if (newVisible.has(userId)) {
      newVisible.delete(userId);
    } else {
      newVisible.add(userId);
    }
    setVisibleUsers(newVisible);
  }

  function handleToggleAllDetails() {
    setShowAllDetails(!showAllDetails);
  }

  function formatLocations(locations: ProcessedListingAddress[]): string {
    if (!locations || locations.length === 0) return "";
    return locations
      .map((location) => {
        const parts = [location.neighborhood, location.city].filter(Boolean);
        return parts.join(", ");
      })
      .join(" • ");
  }

  function renderContactDetailLabels(contact: RealEstateContact) {
    const labels = [];

    if (contact.customerBudget) {
      labels.push(
        <Box key="budget" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Icon icon={currencyFill} width={14} color="green" />
          <Label color="success">{numberToCurrency(contact.customerBudget)}</Label>
        </Box>
      );
    }

    const contactCustomerTypes = getContactCustomerTypes(contact);
    if (contactCustomerTypes.length > 0) {
      labels.push(
        <Box key="type" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Icon icon={personFill} width={14} color="blue" />
          {contactCustomerTypes.map((type) => (
            <ContactTableCustomerChip key={type} type="customerType" customerChipType={type} />
          ))}
        </Box>
      );
    }

    // CRM v2 status chip (status · sub-status + 🔥) — the retired customerSeriousness
    // text is never rendered.
    if (getContactCrmStatus(contact) || getContactCrmIsHot(contact)) {
      labels.push(<ContactCrmStatusChip key="crmStatus" contact={contact} />);
    }

    if (contact.interestedInLocations && contact.interestedInLocations.length > 0) {
      labels.push(
        <Box key="locations" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Icon icon={homeFill} width={14} color="purple" />
          <Label color="secondary">{formatLocations(contact.interestedInLocations)}</Label>
        </Box>
      );
    }

    if (contact.roomsMin || contact.roomsMax) {
      const roomsText =
        contact.roomsMin && contact.roomsMax
          ? `${contact.roomsMin} - ${contact.roomsMax} ${t("search.contacts.roomsLabel", {
              defaultValue: "rooms"
            })}`
          : `${contact.roomsMin || contact.roomsMax} ${t("search.contacts.roomsLabel", {
              defaultValue: "rooms"
            })}`;

      labels.push(
        <Label key="rooms" color="default">
          {roomsText}
        </Label>
      );
    }

    return labels;
  }

  function renderTimelineItem(action: ContactTimelineAction, index: number) {
    if (!visibleActionTypes.has(action.actionType) || !visibleUsers.has(action.actionUser.userId)) {
      return null;
    }

    const iconName = getTimelineIcon(action.actionType);
    const color = getTimelineColor(action.actionType);

    return (
      <StyledTimelineItem key={index}>
        <TimelineOppositeContent sx={{ flex: 0.25, paddingLeft: 0 }}>
          <Tooltip title={formatActionTime(action.actionTime)} arrow>
            <Typography variant="caption" color="text.secondary">
              {formatActionTimeAgo(action.actionTime)}
            </Typography>
          </Tooltip>
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot color={color as any} sx={{ width: 28, height: 28 }}>
            <Icon icon={iconName} width={14} height={14} />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <CompactTimelineContent>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                {t(`contactsPage.timeline.actionTypes.${action.actionType}`, {
                  defaultValue: action.actionType
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                {/* {action.actionType === 'call' ? getTimeBySeconds(action.) : action.actionDescription} */}
                {/* // todo  - use actual data related to the action here*/}
                {action.actionDescription}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <Avatar src={action.actionUser.userAvatar} sx={{ width: 16, height: 16 }}>
                  <Typography sx={{ fontSize: "0.6rem" }}>
                    {action.actionUser.userName.charAt(0).toUpperCase()}
                  </Typography>
                </Avatar>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  {action.actionUser.userName}
                </Typography>
              </Box>
            </Box>
          </CompactTimelineContent>
        </TimelineContent>
      </StyledTimelineItem>
    );
  }

  function renderUserWidgetCard(contactObj: ContactObjectAcrossUsers, index: number) {
    const {
      user,
      contact,
      callsCount,
      messagesSentCount,
      meetingsCount,
      tasksCount,
      lastMeetingTime
    } = contactObj;

    // Calculate favorite listings count from timeline
    const favoriteListingsCount =
      contactDetails
        ?.flatMap((detail) => detail.timeline || [])
        .filter(
          (action) =>
            action.actionType === "favorite-listings-minisite-creation" &&
            action.actionUser.userId === user.userId
        ).length || 0;

    const contactLabels = renderContactDetailLabels(contact);
    const hasDetailedInfo = contactLabels.length > 0 || contact.additionalNotes;

    return (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <UserWidgetCard>
          <CardContent sx={{ pb: hasDetailedInfo ? 1 : 2 }}>
            {/* Header with Avatar and User Info */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Avatar src={user.userAvatar} sx={{ width: 40, height: 40 }}>
                {user.userName.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="medium" noWrap>
                  {user.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.userMail}
                </Typography>
              </Box>
            </Stack>

            {/* Stats */}
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5, mb: 2 }}>
              {callsCount > 0 && (
                <StatsChip
                  size="small"
                  label={`${callsCount} ${t("contactsPage.timeline.calls", {
                    defaultValue: "calls"
                  })}`}
                  color="success"
                  variant="outlined"
                />
              )}
              {messagesSentCount > 0 && (
                <StatsChip
                  size="small"
                  label={`${messagesSentCount} ${t("contactsPage.timeline.messages", {
                    defaultValue: "messages"
                  })}`}
                  color="primary"
                  variant="outlined"
                />
              )}
              {meetingsCount > 0 && (
                <StatsChip
                  size="small"
                  label={`${meetingsCount} ${t("contactsPage.timeline.meetings", {
                    defaultValue: "meetings"
                  })}`}
                  color="info"
                  variant="outlined"
                />
              )}
              {tasksCount > 0 && (
                <StatsChip
                  size="small"
                  label={`${tasksCount} ${t("contactsPage.timeline.tasks", {
                    defaultValue: "tasks"
                  })}`}
                  color="warning"
                  variant="outlined"
                />
              )}
              {favoriteListingsCount > 0 && (
                <StatsChip
                  size="small"
                  label={t("contactsPage.timeline.sentMinisite", {
                    defaultValue: "sent minisite"
                  })}
                  color="error"
                  variant="outlined"
                />
              )}
            </Stack>

            {/* Last Meeting */}
            {lastMeetingTime && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("contactsPage.timeline.lastMeeting", { defaultValue: "Last meeting" })}:{" "}
                {formatActionTime(lastMeetingTime)}
              </Typography>
            )}
          </CardContent>

          {/* Expanded Details */}
          {hasDetailedInfo && (
            <Collapse in={showAllDetails}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />

                {/* Contact Details with Labels */}
                {contactLabels.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      {t("contactsPage.timeline.contactDetails", {
                        defaultValue: "Contact Details"
                      })}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                      {contactLabels}
                    </Stack>
                  </Box>
                )}

                {/* Notes */}
                {contact.additionalNotes && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                      <Icon icon={fileTextFill} width={14} height={14} color="grey.500" />
                      <Typography variant="caption" color="text.secondary">
                        {t("contactsPage.timeline.notes", { defaultValue: "Notes" })}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        fontSize: "0.8rem",
                        bgcolor: "grey.50",
                        p: 1.5,
                        borderRadius: 1,
                        border: 1,
                        borderColor: "grey.200"
                      }}
                    >
                      {contact.additionalNotes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          )}
        </UserWidgetCard>
      </Grid>
    );
  }

  if (loading) {
    return (
      <Box>
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Skeleton variant="circular" width={48} height={48} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="40%" height={16} />
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Skeleton
                        variant="rectangular"
                        width={60}
                        height={24}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={24}
                        sx={{ borderRadius: 1 }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!contactDetails || contactDetails.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t("contactsPage.timeline.noData", {
            defaultValue: "No timeline data available for this contact"
          })}
        </Typography>
      </Box>
    );
  }

  // Get all unique users and action types for filters
  const allUsers = new Map<string, { name: string; avatar: string }>();
  const allActionTypes = new Set<string>();

  contactDetails.forEach((detail) => {
    detail.timeline?.forEach((action) => {
      allUsers.set(action.actionUser.userId, {
        name: action.actionUser.userName,
        avatar: action.actionUser.userAvatar
      });
      allActionTypes.add(action.actionType);
    });
  });

  const filteredTimeline = contactDetails
    .flatMap(
      (detail) =>
        detail.timeline?.filter(
          (action) =>
            visibleActionTypes.has(action.actionType) && visibleUsers.has(action.actionUser.userId)
        ) || []
    )
    .sort((a, b) => {
      if (!a.actionTime) return 1;
      if (!b.actionTime) return -1;
      return new Date(b.actionTime).getTime() - new Date(a.actionTime).getTime();
    });

  return (
    <Box>
      {contactDetails.map((contactDetail, contactIndex) => (
        <Box key={contactIndex} sx={{ mb: 4 }}>
          {/* Contact Objects Across Users */}
          {contactDetail.contactObjectsAcrossUsers?.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {/* Title Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  mb: { xs: 2, sm: 3 }
                }}
                onClick={() => setShowContactDetails(!showContactDetails)}
              >
                <Typography variant="h6">
                  {t("contactsPage.timeline.contactAcrossUsers", {
                    defaultValue: "Contact Details Across Organization"
                  })}
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    transition: "transform 0.2s ease-in-out",
                    pointerEvents: "none"
                  }}
                >
                  <Icon
                    icon={showContactDetails ? arrowIosUpwardFill : arrowIosDownwardFill}
                    width={20}
                    height={20}
                  />
                </IconButton>
              </Box>

              {/* Centralized More Details Button - Separate line on mobile */}
              {showContactDetails &&
                contactDetail.contactObjectsAcrossUsers.some((contactObj) => {
                  const contactLabels = renderContactDetailLabels(contactObj.contact);
                  return contactLabels.length > 0 || contactObj.contact.additionalNotes;
                }) && (
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      justifyContent: { xs: "center", sm: "flex-end" }
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleToggleAllDetails}
                      startIcon={
                        <Icon
                          icon={showAllDetails ? arrowIosUpwardFill : arrowIosDownwardFill}
                          width={16}
                          height={16}
                        />
                      }
                      sx={{ textTransform: "none" }}
                    >
                      {showAllDetails
                        ? t("contactsPage.timeline.hideAllDetails", {
                            defaultValue: "Hide All Details"
                          })
                        : t("contactsPage.timeline.showAllDetails", {
                            defaultValue: "Show All Details"
                          })}
                    </Button>
                  </Box>
                )}

              <Collapse in={showContactDetails}>
                <Grid container spacing={2}>
                  {contactDetail.contactObjectsAcrossUsers.map(renderUserWidgetCard)}
                </Grid>
              </Collapse>
            </Box>
          )}

          {/* Timeline Section */}
          {contactDetail.timeline?.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2
                }}
              >
                <Typography variant="h6">
                  {t("contactsPage.timeline.activityTimeline", {
                    defaultValue: "Activity Timeline"
                  })}
                </Typography>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                  sx={{
                    bgcolor: showFilters ? "primary.main" : "transparent",
                    color: showFilters ? "white" : "inherit",
                    "&:hover": {
                      bgcolor: showFilters ? "primary.dark" : "action.hover"
                    }
                  }}
                >
                  <Icon icon={filterFill} width={20} height={20} />
                </IconButton>
              </Box>

              <Collapse in={showFilters}>
                <FilterSection>
                  <Stack spacing={3}>
                    {/* Action Type Filters */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("contactsPage.timeline.filterByActionType", {
                          defaultValue: "Filter by Action Type"
                        })}
                      </Typography>
                      <FormGroup row>
                        {ACTION_TYPES.filter((type) => allActionTypes.has(type)).map(
                          (actionType) => (
                            <FormControlLabel
                              style={{ marginRight: 5, marginLeft: 5 }}
                              key={actionType}
                              control={
                                <Checkbox
                                  checked={visibleActionTypes.has(actionType)}
                                  onChange={() => handleActionTypeToggle(actionType)}
                                  size="small"
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Icon
                                    icon={getTimelineIcon(actionType)}
                                    width={16}
                                    height={16}
                                    color={`var(--palette-${getTimelineColor(actionType)}-main)`}
                                  />
                                  <Typography variant="body2">
                                    {t(`contactsPage.timeline.actionTypes.${actionType}`, {
                                      defaultValue: actionType
                                    })}
                                  </Typography>
                                </Box>
                              }
                            />
                          )
                        )}
                      </FormGroup>
                    </Box>

                    {/* User Filters */}
                    {allUsers.size > 1 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("contactsPage.timeline.filterByUser", {
                            defaultValue: "Filter by User"
                          })}
                        </Typography>
                        <FormGroup row>
                          {Array.from(allUsers.entries()).map(([userId, user]) => (
                            <FormControlLabel
                              style={{ marginRight: 5, marginLeft: 5 }}
                              key={userId}
                              control={
                                <Checkbox
                                  checked={visibleUsers.has(userId)}
                                  onChange={() => handleUserToggle(userId)}
                                  size="small"
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Avatar src={user.avatar} sx={{ width: 16, height: 16 }}>
                                    <Typography sx={{ fontSize: "0.6rem" }}>
                                      {user.name.charAt(0).toUpperCase()}
                                    </Typography>
                                  </Avatar>
                                  <Typography variant="body2">{user.name}</Typography>
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    )}
                  </Stack>
                </FilterSection>
              </Collapse>

              {filteredTimeline.length > 0 ? (
                <Timeline>
                  {filteredTimeline.map((action, index) => renderTimelineItem(action, index))}
                </Timeline>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("contactsPage.timeline.noFilteredResults", {
                      defaultValue: "No timeline items match the current filters"
                    })}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
